import { Button, Input, Select } from "antd";
import { TableProps } from "antd/es/table";
import { Link, useLocation } from "react-router-dom";
import { useAppSelector } from "../../../app/store";
import DownloadIcon from "../../../assets/icons/DownloadIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import SettingIcon from "../../../assets/icons/SettingIcon";
import SortIcon from "../../../assets/icons/SortIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import ReportRequestModal from "../../../components/Report/ReportRequestModal";
import CustomTable from "../../../components/ui/CustomTable";
import { employeeSearchTypeList } from "../../../constants/constants";
import { useGetDepartmentsQuery } from "../../../features/department/departmentApi";
import { useGetEmployeesQuery } from "../../../features/employee/employeeApi";
import { useEmployeeLeaveBalanceDetailsReportMutation } from "../../../features/report/reportApi";
import useDebounce from "../../../hooks/useDebounce";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { EmployeeData, GetEmployeeParamsType } from "../../../types";
import { countParPageSerialNumber } from "../../../utils/number";
import {
  addKeyInArray,
  selectArrayOption,
  selectSearchOption,
  selectStringOption,
  skipData,
  underscoreToCapitalizeString,
} from "../../../utils/utils";

export type LeaveBalanceFilterType = Omit<
  GetEmployeeParamsType,
  "limit" | "skip" | "search" | "business_role_id"
>;

const searchTypeSelect = employeeSearchTypeList?.map((item) => ({
  label: underscoreToCapitalizeString(item),
  value: item,
}));

enum Status {
  active = "status-success",
  inactive = "status-danger",
}

export default function LeaveBalance() {
  const location = useLocation();
  const { keyword, handleInputChange } = useDebounce();
  const { authInfo } = useAppSelector((state) => state.auth);
  const { open, handleClose, handleOpen } = useModal<
    string,
    "REPORT_REQUEST"
  >();
  const { page, limit, handlePagination, handleLimit, filter, handleFilter } =
    useTable<LeaveBalanceFilterType>({
      defaultFilter: {
        status: "active",
        search_type: "name",
      },
    });
  const {
    isError: isDepartmentError,
    error: departmentError,
    data: departmentData,
  } = useGetDepartmentsQuery({
    params: { skip: 0, limit: 0, is_published: 1 },
  });
  const { isLoading, isError, error, data } = useGetEmployeesQuery({
    params: {
      limit,
      skip: skipData(page, limit),
      search_type: undefined,
      ...(keyword ? { search_type: filter.search_type } : {}),
      search: keyword,
      status: "active",
      ...filter,
      manager_id: authInfo?.role === "Manager" ? authInfo?.id : undefined,
    },
  });
  const [
    employeeLeaveBalanceDetailsReport,
    {
      isLoading: isGenerateLoading,
      isError: isGenerateError,
      error: generateError,
      isSuccess: isGenerateSuccess,
    },
  ] = useEmployeeLeaveBalanceDetailsReportMutation();
  useError(isGenerateError, generateError, "Generate request report fail");
  useError(isError, error, "Get employee data fail");
  useError(isDepartmentError, departmentError, "Get department data fail");

  const columns: TableProps<EmployeeData>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Employee ID",
      dataIndex: "employee_id",
      render: (val) => val ?? "n/a",
    },
    {
      title: "Employee Name",
      dataIndex: "member",
      sorter: (a, b) =>
        a?.member?.profile?.name?.localeCompare(b?.member?.profile?.name),
      sortIcon: () => <SortIcon />,
      render: (val) => val?.profile?.name ?? "n/a",
    },
    {
      title: "Email Id",
      dataIndex: "member",
      render: (val) => val?.profile?.email ?? "n/a",
    },
    // {
    //   title: "ID",
    //   dataIndex: "id",
    // },
    {
      title: "Department",
      dataIndex: "role",
      render: (val) => val?.department?.name ?? "n/a",
    },
    {
      title: "Action",
      dataIndex: "id",
      render: (val) => (
        <Link to={`${location.pathname}/view/${val}`}>
          <Button className="action-button" size="small" icon={<ViewIcon />} />
        </Link>
      ),
    },
  ];

  return (
    <SidebarLayout title="Leave Balance">
      <CustomTable
        loading={isLoading}
        limit={limit}
        page={page}
        columns={columns}
        total={data?.data?.totalLength}
        dataSource={addKeyInArray(data?.data?.employees)}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        filterSection={
          <>
            <Select
              showSearch
              placeholder="Search Type"
              value={filter?.search_type}
              options={searchTypeSelect}
              onChange={(val) => handleFilter("search_type", val)}
              filterOption={selectSearchOption}
              className="min-w-[100px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
            />
            <Input
              key={filter.search_type}
              suffix={<SearchIcon />}
              placeholder={`Search by ${underscoreToCapitalizeString(
                filter?.search_type || "name",
                true
              )}`}
              style={{ maxWidth: 220 }}
              onChange={(val) => {
                handleInputChange(val);
              }}
            />
            {/* <RangePicker
              defaultValue={[
                dayjs(formatDate(), dateFormat),
                dayjs(formatDate(), dateFormat),
              ]}
              format={dateFormat}
              style={{ width: 260 }}
              suffixIcon={<DateIcon />}
            /> */}
            <Select
              allowClear
              showSearch
              placeholder="Department"
              filterOption={selectSearchOption}
              options={selectArrayOption(
                departmentData?.data?.departments || [],
                "name",
                "id"
              )}
              className="min-w-[130px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
              onChange={(val) => handleFilter("business_department_id", val)}
            />
            <Select
              showSearch
              placeholder="Status"
              filterOption={selectSearchOption}
              options={selectStringOption(Object.keys(Status), "string", true)}
              className="min-w-[90px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
              value={filter?.status}
              onChange={(val) => handleFilter("status", val)}
            />
            {/* <Button
              className="download-button"
              icon={<DownloadIcon />}
              loading={excelLoading}
              onClick={handleDownload}
            >
              Export
            </Button> */}
            <Button
              onClick={() => handleOpen({ type: "REPORT_REQUEST" })}
              className="download-button"
              icon={<DownloadIcon />}
            >
              Report Requests
            </Button>
            <Link to="/admin/settings/leave/leave-prorate">
              <Button
                type="primary"
                className="middle-button !p-4"
                icon={<SettingIcon />}
              />
            </Link>
          </>
        }
      />

      {open.type === "REPORT_REQUEST" && (
        <ReportRequestModal
          open={open.open}
          handleClose={handleClose}
          isLoading={isGenerateLoading}
          isSuccess={isGenerateSuccess}
          handleSubmit={(params: any) => {
            employeeLeaveBalanceDetailsReport({
              body: {
                name: "All user leave balance report",
              },
              params: {
                ...filter,
                ...params,
                skip: 0,
                limit: 0,
                manager_id:
                  authInfo?.role === "Manager" ? authInfo?.id : undefined,
              },
            });
          }}
        />
      )}

      {/* {open.type === "REPORT_REQUEST" && (
        <RequestDateWithOutDateModal
          open={open.open}
          handleClose={handleClose}
          isLoading={isGenerateLoading}
          isSuccess={isGenerateSuccess}
          // detailsMessage={`You are requesting a Report Export range for ${dayjs(
          //   filter?.date,
          //   dbDateFormat
          // ).format("DD/MM/YYYY")}`}
          handleSubmit={() => {
            employeeLeaveBalanceDetailsReport({
              body: {
                name: "All user leave balance report",
              },
              params: { ...filter, skip: 0, limit: 0 },
            });
          }}
        />
      )} */}
    </SidebarLayout>
  );
}
